// THE TURF'S OWN TOP (the owner's picks, Oct 12, the "Turf Looks" page): TOP 'clover' and TUFT 'stars', with "no small
// swaying tuft kubes — just tuft texture". Until then turf wore its regolith's pattern (the sand grains) under its colour,
// and grass tufts of micro-kubes stood on it; now a turfed top wears CLOVER (little three-leaf rosettes, black over white,
// multiplied by the turf's colour) with STAR TUFTS painted over it (five-bladed stars, a lighter blade over a darker
// shadow, both tones of the turf's colour), and from world version 15 no grass tufts stand on turf (flora.mjs).
//
// ONE CANVAS FOR EVERY TURF COLOUR. The turf's colour is the world's (turf.mjs turfHex, 32 of them), and the tufts' two
// tones are that colour's own, so nothing here can be baked in a colour. The canvas carries three numbers per texel
// instead, which the shader (system-view.js TURF_GLSL) and the icon (app.js turfIconPaint) put together with the colour:
//   R  the clover: 1 where there is none, darker under a rosette (black over white, as the page drew it);
//   G  how much of the texel the tufts' SHADOW stroke covers (0..1);
//   B  how much of it their BLADE stroke covers (0..1).
//   colour = mix(mix(turf × R, shadow tone, G), blade tone, B)
// which is the page's own compositing (the clover multiplied into the turf's colour, then each tuft stroked over it, its
// shadow first and its blade on top) with the colour left out. The page drew a shadow and then its blade star by star,
// so where two tufts overlap a later shadow could cross an earlier blade; here a blade is always on top. Seventy tufts
// to a kube seldom meet.
//
// DRAWN WITH DRAW CALLS ALONE (tests/material-looks.test.mjs: a texture maker does no per-pixel pass). Each channel is
// written through a blend mode that leaves the other two alone: the canvas starts (255, 0, 0); a rosette is a 'multiply'
// fill in rgba(0, 255, 255, a), which darkens red by its alpha and keeps green and blue (anything × 1); a stroke is a
// 'screen' stroke in pure green or pure blue, which takes its channel towards 1 by its coverage (screen with 1 is 1) and
// keeps the others (screen with 0 is the colour itself). From fixed seeds, the page's own (5 for the top pattern, 91 for
// the tufts), and its own draws in its own order, at 1024 texels to a kube where the page drew 192: the layout the owner
// picked, every rosette and tuft where it was, 16/3 times the size. Every shape is drawn again across the edges it
// crosses, so the canvas tiles and the clover runs on from kube to kube.
//
// THREE-free, and it returns a canvas: the game and the icons each wrap it in a texture of their own (NoColorSpace: the
// numbers are data, not colours), and the shader treats R as the page did, in the display's encoding (sRGB-decoded
// before it multiplies, so the clover is as dark on screen as on the page).

export const TURF_TOP_SIZE = 1024;
const PAGE = 192, S = TURF_TOP_SIZE / PAGE;                  // the page's kube, and its scale up to the game's
export const TURF_TOP_SEED = 5, TURF_TUFT_SEED = 91;         // the page's seeds (turf.html patternCanvas(TOPS, …, 5), rng(91))
export const CLOVER_N = 90, TUFT_N = 70;                     // rosettes and tufts to a kube

// The page's generator (an LCG), so the same seed lays out the same rosettes and tufts.
function rng(seed) { let s = seed >>> 0; return () => ((s = (Math.imul(s, 1664525) + 1013904223) >>> 0) / 4294967296); }
// Calls fn(X, Y) at (x, y) and at each copy across an edge it reaches (r), as the page's wrap() did.
function wrap(fn, x, y, r) {
    const N = TURF_TOP_SIZE;
    for (const dx of [-N, 0, N]) for (const dy of [-N, 0, N]) {
        const X = x + dx, Y = y + dy;
        if (X + r < 0 || Y + r < 0 || X - r > N || Y - r > N) continue;
        fn(X, Y);
    }
}

export function drawTurfTop() {
    const N = TURF_TOP_SIZE, canvas = document.createElement('canvas');
    canvas.width = canvas.height = N;
    const ctx = canvas.getContext('2d');
    ctx.fillStyle = 'rgb(255, 0, 0)';
    ctx.fillRect(0, 0, N, N);
    // CLOVER (the page's TOPS 'clover'): ninety rosettes of three round leaves 1.7 across at 2.2 from their middle, each
    // in its own share of black (0.15-0.45), every leaf its own fill so where two overlap they darken together.
    const C = rng(TURF_TOP_SEED);
    ctx.globalCompositeOperation = 'multiply';
    for (let i = 0; i < CLOVER_N; i++) {
        const x = C() * N, y = C() * N, a0 = C() * 6.3, a = 0.15 + C() * 0.3;
        ctx.fillStyle = `rgba(0, 255, 255, ${a.toFixed(4)})`;
        wrap((X, Y) => {
            for (let k = 0; k < 3; k++) {
                const t = a0 + k * 2.094;
                ctx.beginPath(); ctx.arc(X + Math.cos(t) * 2.2 * S, Y + Math.sin(t) * 2.2 * S, 1.7 * S, 0, 6.2832); ctx.fill();
            }
        }, x, y, 5 * S);
    }
    // STAR TUFTS (the page's TUFTS 'stars'): seventy five-bladed stars, the shadow 3.4 long set 0.8 down and right of the
    // blade 3.2 long, both 1.2 wide, at the same angle (0.3).
    const T = rng(TURF_TUFT_SEED);
    ctx.globalCompositeOperation = 'screen';
    ctx.lineWidth = 1.2 * S;
    const star = (x, y, r) => {
        ctx.beginPath();
        for (let k = 0; k < 5; k++) { const a = 0.3 + k * 6.283 / 5; ctx.moveTo(x, y); ctx.lineTo(x + Math.cos(a) * r, y + Math.sin(a) * r); }
        ctx.stroke();
    };
    for (let i = 0; i < TUFT_N; i++) {
        const x = T() * N, y = T() * N;
        wrap((X, Y) => {
            ctx.strokeStyle = 'rgb(0, 255, 0)'; star(X + 0.8 * S, Y + 0.8 * S, 3.4 * S);
            ctx.strokeStyle = 'rgb(0, 0, 255)'; star(X, Y, 3.2 * S);
        }, x, y, 10 * S);
    }
    ctx.globalCompositeOperation = 'source-over';
    return canvas;
}
// One canvas a page: the world and the icons share it (each uploads it to its own renderer).
let _canvas = null;
export const turfTopCanvas = () => _canvas || (_canvas = drawTurfTop());

// THE TUFTS' TWO TONES of a turf colour, as the page made them (three.js r128 Color.offsetHSL, in the display's sRGB,
// which r128 worked in): the BLADE lighter, hue +0.02, saturation +0.05, lightness +0.16; the SHADOW darker, lightness
// −0.18. Each clamped as setHSL clamps it, so white turf keeps white blades and Void's near-black a black shadow.
// '#rrggbb' in, { blade, shadow } as '#rrggbb' out.
export function turfTones(hex) {
    const n = parseInt(String(hex || '#228B22').replace('#', ''), 16), r = ((n >> 16) & 255) / 255, g = ((n >> 8) & 255) / 255, b = (n & 255) / 255;
    const max = Math.max(r, g, b), min = Math.min(r, g, b), l = (min + max) / 2;
    let h = 0, s = 0;
    if (min !== max) {
        const d = max - min;
        s = l <= 0.5 ? d / (max + min) : d / (2 - max - min);
        h = max === r ? (g - b) / d + (g < b ? 6 : 0) : max === g ? (b - r) / d + 2 : (r - g) / d + 4;
        h /= 6;
    }
    return { blade: hslHex(h + 0.02, s + 0.05, l + 0.16), shadow: hslHex(h, s, l - 0.18) };
}
function hslHex(h, s, l) {
    h = ((h % 1) + 1) % 1; s = Math.min(1, Math.max(0, s)); l = Math.min(1, Math.max(0, l));
    const hue = (p, q, t) => { if (t < 0) t += 1; if (t > 1) t -= 1; if (t < 1 / 6) return p + (q - p) * 6 * t; if (t < 1 / 2) return q; if (t < 2 / 3) return p + (q - p) * 6 * (2 / 3 - t); return p; };
    let r = l, g = l, b = l;
    if (s !== 0) { const p = l <= 0.5 ? l * (1 + s) : l + s - l * s, q = 2 * l - p; r = hue(q, p, h + 1 / 3); g = hue(q, p, h); b = hue(q, p, h - 1 / 3); }
    const c = (v) => Math.floor(Math.min(1, Math.max(0, v)) * 255).toString(16).padStart(2, '0');   // r128 getHex truncates
    return '#' + c(r) + c(g) + c(b);
}
